import * as Sentry from "@sentry/react";
import { Button } from "@/ui-components/ui/button";
import { ReloadIcon } from "@radix-ui/react-icons";
import { AlertCircle, Bug } from "lucide-react";
import { Component, ErrorInfo, ReactNode } from "react";
import { AuroraBackground } from "@/ui-components/ui/shadcn-io/aurora-background";
import { useErrorReport } from "@/lib/hooks/use-error-report";
import { getCurrentTraceId } from "@/lib/monitoring/sentry";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  traceId?: string;
}

// Inner component to use hooks
const ErrorContent = ({ error, traceId }: { error?: Error; traceId?: string }) => {
  const { sendReport } = useErrorReport();

  return (
    <div className="h-full w-full bg-white">
      <AuroraBackground variant="red">
        <div className="w-full max-w-2xl px-6 relative z-10">
          <div className="mx-auto flex flex-col items-center text-center">
            <div className="mb-6 grid place-items-center rounded-2xl bg-white/70 p-4 ring-1 ring-black/5 backdrop-blur">
              <AlertCircle className="h-9 w-9 text-red-600" />
            </div>

            <h3 className="text-xl font-bold text-gray-900 mb-2 tracking-tight">Ein Fehler ist aufgetreten</h3>
            <p className="text-gray-500 max-w-md mx-auto leading-relaxed">
              Es ist leider ein Fehler aufgetreten. Bitte versuchen Sie es später erneut oder kontaktieren Sie den Support.
            </p>
            {traceId && <p className="mt-3 text-xs text-gray-500">Trace-ID: {traceId}</p>}

            <div className="flex flex-col sm:flex-row gap-2 justify-center w-full mt-6">
              <Button 
                onClick={() => window.location.reload()} 
                className="w-full sm:w-auto h-11 px-8 rounded-xl bg-red-600 hover:bg-red-700 text-white shadow-sm transition-all flex items-center gap-2"
              >
                <ReloadIcon className="w-4 h-4" />
                Seite neu laden
              </Button>
              <Button 
                variant="outline" 
                className="w-full sm:w-auto h-11 px-8 rounded-xl border-gray-200 flex items-center gap-2"
                onClick={() => error && sendReport(error, { traceId })}
              >
                <Bug className="h-4 w-4" />
                Fehlerbericht senden
              </Button>
            </div>
          </div>
        </div>
      </AuroraBackground>
    </div>
  );
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const traceId = getCurrentTraceId();
    this.setState({ traceId });
    Sentry.withScope((scope) => {
      scope.setContext("react_error_boundary", {
        componentStack: errorInfo.componentStack,
        traceId,
      });
      Sentry.captureException(error);
    });
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorContent error={this.state.error} traceId={this.state.traceId} />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
